import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSBox from "../../system-design/DSBox";
import useCollectionsList from "../hooks/useCollectionsList";
import { CollectionStatus } from "../hooks/usePublicCollectionsInfiniteQuery";
import DSTypography from "../../system-design/DSTypography";
import FilterBar from "components/FilterBar";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import {
  Column,
  columnKey,
} from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import CollectionsListAvatar from "./CollectionsListAvatar";
import { useState } from "react";
import CollectionDialog from "./CollectionDialog";
import DSEnumSelect from "../../system-design/DSEnumSelect";
import DSTooltip from "../../system-design/DSTooltip";
import {
  PopulatedApprovedCollection,
  MarketplaceStatus,
} from "../../../api/collection";
import {
  isDueForScheduledPublish,
  scheduledMarketplaceLabel,
} from "../utils/marketplaceSchedule";
import usePublishPendingCollectionsMutation from "../hooks/usePublishPendingCollectionsMutation";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSAlert from "../../system-design/DSAlert";
import ConfirmationModal from "../../ConfirmationModal";
import MultiSelectFilter from "../../../components/MultiSelectFilter";
import DSCheckbox from "../../system-design/DSCheckBox";

const MARKETPLACE_FILTER_OPTIONS: {
  value: string;
  label: string;
}[] = [
  { value: MarketplaceStatus.Pending, label: "Pending" },
  { value: MarketplaceStatus.True, label: "Yes" },
  { value: MarketplaceStatus.False, label: "No" },
];

const YES_NO_ALL_OPTIONS: { value: "ALL" | "TRUE" | "FALSE"; label: string }[] =
  [
    { value: "ALL", label: "All" },
    { value: "TRUE", label: "Yes" },
    { value: "FALSE", label: "No" },
  ];

const STATUS_FILTER_OPTIONS: {
  value: CollectionStatus;
  label: string;
}[] = [
  { value: "approved", label: "Approved" },
  { value: "draft", label: "Draft" },
];

type ApprovedCollectionsProps = {
  // "waiting" locks the list to approved collections that are not in the
  // marketplace yet, and hides the status/marketplace filters.
  waiting?: boolean;
};

const ApprovedCollections = ({ waiting = false }: ApprovedCollectionsProps) => {
  const {
    data: collections,
    fetchNextPage,
    isLoading,
    handleTextSearch,
    handleSort,
    sort,
    columns,
    statusFilter,
    setStatusFilter,
    marketplaceFilter,
    setMarketplaceFilter,
    licensingFilter,
    setLicensingFilter,
    hasEtaFilter,
    setHasEtaFilter,
    textSearch,
  } = useCollectionsList(10, waiting ? [MarketplaceStatus.Pending] : undefined);

  const isDraft = statusFilter === "draft";

  const [chosenCollection, setChosenCollection] = useState("");
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);

  const {
    mutateAsync: publishPendingCollections,
    data: publishResult,
    isPending: isPublishing,
    error: publishError,
    reset: resetPublish,
  } = usePublishPendingCollectionsMutation();

  // The cron publishes everything already due, not only today's schedule, so
  // the button is offered whenever the visible list has at least one due row.
  const dueCount = (collections ?? []).filter((c) =>
    isDueForScheduledPublish(c.marketplace),
  ).length;

  const handleConfirmPublish = async () => {
    setIsPublishConfirmOpen(false);
    try {
      await publishPendingCollections();
    } catch {
      // surfaced through `publishError`
    }
  };

  const activeFilterCount = waiting
    ? (licensingFilter !== "ALL" ? 1 : 0) + (hasEtaFilter ? 1 : 0)
    : (marketplaceFilter?.length ? 1 : 0) +
      (licensingFilter !== "ALL" ? 1 : 0) +
      (isDraft ? 1 : 0);

  const buildTableRows = (
    collection: PopulatedApprovedCollection,
    columns: Column<PopulatedApprovedCollection>[],
  ) => (
    <>
      {columns.map((col) => {
        // Two columns render off `marketplace`, so branch on identity.
        const key = columnKey(col);
        return (
          <DSTableCell
            key={key}
            style={{
              width: col.size || `${100 / columns?.length}%`,
              whiteSpace: "nowrap",
            }}
          >
            {key === "coverUrl" ? (
              <CollectionsListAvatar collectionCover={collection.coverUrl} />
            ) : isDraft &&
              (key === "marketplace" ||
                key === "marketplace.publishedAt" ||
                key === "productPageMetadata" ||
                key === "totalPurchases") ? (
              "-"
            ) : key === "marketplace.publishedAt" ? (
              collection.marketplace?.publishedAt ? (
                new Date(
                  collection.marketplace.publishedAt,
                ).toLocaleDateString()
              ) : (
                "-"
              )
            ) : key === "marketplace" ? (
              collection.marketplace?.status === MarketplaceStatus.True ? (
                "Published"
              ) : collection.marketplace?.status ===
                  MarketplaceStatus.Pending && collection.marketplace?.date ? (
                scheduledMarketplaceLabel(collection.marketplace.date)
              ) : collection.marketplace?.status ===
                MarketplaceStatus.Pending ? (
                "Pending"
              ) : (
                "No"
              )
            ) : key === "productPageMetadata" ? (
              collection?.plan ? (
                `$${collection.plan.price} · ${collection.plan.revSharePercentage > 0 ? `${collection.plan.revSharePercentage}% rev share` : "No rev share"}`
              ) : (
                "-"
              )
            ) : key === "totalPurchases" ? (
              collection.totalPurchases || 0
            ) : key === "creator" ? (
              collection.creator ? (
                `${collection.creator.name}`
              ) : (
                "N/A"
              )
            ) : (
              String(collection[col.field])
            )}
          </DSTableCell>
        );
      })}
    </>
  );

  return (
    <>
      <FilterBar
        search={{
          defaultValue: textSearch,
          onChange: handleTextSearch,
          placeholder: "Search collections, creator name or id",
        }}
        activeCount={activeFilterCount}
        quickActions={
          waiting && (
            <DSPrimaryButton
              size="small"
              disabled={isPublishing}
              onClick={() => {
                resetPublish();
                setIsPublishConfirmOpen(true);
              }}
              startIcon={
                isPublishing ? (
                  <DSCircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Publish today&apos;s collections
            </DSPrimaryButton>
          )
        }
      >
        {!waiting && (
          <DSBox display="flex" alignItems="center" gap={1}>
            <DSTypography variant="caption">Status:</DSTypography>
            <DSEnumSelect
              size="small"
              sx={{ minWidth: 100 }}
              value={statusFilter ?? "approved"}
              options={STATUS_FILTER_OPTIONS}
              onChange={setStatusFilter}
            />
          </DSBox>
        )}
        {!waiting && (
          <DSBox display="flex" alignItems="center" gap={1}>
            <DSTypography variant="caption">Marketplace:</DSTypography>
            <DSTooltip
              title={
                isDraft
                  ? "Marketplace filter applies to approved collections only"
                  : ""
              }
            >
              <span>
                <MultiSelectFilter
                  selected={marketplaceFilter ?? []}
                  options={MARKETPLACE_FILTER_OPTIONS}
                  onChange={(val) =>
                    setMarketplaceFilter(val as MarketplaceStatus[])
                  }
                  allLabel="All"
                  disabled={isDraft}
                />
              </span>
            </DSTooltip>
          </DSBox>
        )}
        <DSBox display="flex" alignItems="center" gap={1}>
          <DSTypography variant="caption">Licensing:</DSTypography>
          <DSEnumSelect
            size="small"
            sx={{ minWidth: 100 }}
            value={licensingFilter ?? "ALL"}
            options={YES_NO_ALL_OPTIONS}
            onChange={setLicensingFilter}
          />
        </DSBox>
        {waiting && (
          <DSBox display="flex" alignItems="center">
            <DSCheckbox
              size="small"
              checked={hasEtaFilter}
              onChange={(e) => setHasEtaFilter(e.target.checked)}
            />
            <DSTypography variant="caption" lineHeight={0}>
              Has ETA only
            </DSTypography>
          </DSBox>
        )}
      </FilterBar>
      {publishResult && (
        <DSAlert
          severity={publishResult.failed > 0 ? "warning" : "success"}
          sx={{ mb: 2 }}
          onClose={resetPublish}
        >
          {`Published ${publishResult.published}, skipped ${publishResult.skipped}, failed ${publishResult.failed}`}
        </DSAlert>
      )}
      {publishError && (
        <DSAlert severity="error" sx={{ mb: 2 }} onClose={resetPublish}>
          {publishError.message || "Failed to publish pending collections"}
        </DSAlert>
      )}
      {collections && !isLoading ? (
        <>
          <DSBaseTable
            columns={columns}
            onPressRow={({ id }: PopulatedApprovedCollection) => {
              setChosenCollection(id);
            }}
            rowBuilder={buildTableRows}
            fetchMore={fetchNextPage}
            data={collections}
            onSort={handleSort}
            sortState={sort}
          />
        </>
      ) : (
        !isLoading && (
          <DSTypography variant="body1" textAlign="center">
            <p>No Collections found</p>
          </DSTypography>
        )
      )}
      {isLoading && (
        <DSBox
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DSCircularProgress />
        </DSBox>
      )}
      <ConfirmationModal
        open={isPublishConfirmOpen}
        message={
          dueCount > 0
            ? `Publish ${dueCount} due collection${dueCount > 1 ? "s" : ""} to the marketplace now? This runs the same pass as the hourly cron and makes the products live on Shopify.`
            : "No collection in this list is due yet. Run the publication pass anyway?"
        }
        onClose={() => setIsPublishConfirmOpen(false)}
        onConfirm={handleConfirmPublish}
      />
      {chosenCollection && (
        <CollectionDialog
          fullWidth
          maxWidth="xl"
          collectionId={chosenCollection}
          open={!!chosenCollection}
          onClose={() => setChosenCollection("")}
        />
      )}
    </>
  );
};

export default ApprovedCollections;
